/**
 * P1-POLL-01 — Central global cadence gate for live polls.
 *
 * Single source of truth that decides whether ANY poll engine
 * (LivePollWidget, MatchPulse, FanDataPulse, SponsoredSurveyScheduler,
 * SurveyIntelligence) is allowed to surface a new poll right now.
 *
 * The gate enforces:
 *   - minimum global gap between polls
 *   - cooldown after dismiss
 *   - cooldown after vote
 *   - cooldown after Attention yield (poll is preserved, not lost)
 *   - per-session cap
 *   - per-match cap
 *
 * No backend, no new tables — pure in-memory + sessionStorage state.
 */

export interface PollCadencePolicy {
  minGapBetweenPollsSec: number;
  afterDismissCooldownSec: number;
  afterVoteCooldownSec: number;
  afterAttentionYieldCooldownSec: number;
  minVisibleBeforeYieldSec: number;
  maxPollsPerSession: number;
  maxPollsPerMatch: number;
}

export const POLL_CADENCE_POLICY: PollCadencePolicy = {
  minGapBetweenPollsSec: 90,
  afterDismissCooldownSec: 180,
  afterVoteCooldownSec: 120,
  afterAttentionYieldCooldownSec: 60,
  minVisibleBeforeYieldSec: 12,
  maxPollsPerSession: 8,
  maxPollsPerMatch: 10,
};

type Reason = "dismiss" | "vote" | "attention_yield" | "shown" | null;

interface GateState {
  lastShownAt: number;
  cooldownUntil: number;
  cooldownReason: Reason;
  sessionShown: number;
  matchShown: number;
  matchId: string | null;
}

const STORAGE_KEY = "vs:poll_cadence_gate_v1";

function now(): number {
  return Date.now();
}

function emptyState(): GateState {
  return {
    lastShownAt: 0,
    cooldownUntil: 0,
    cooldownReason: null,
    sessionShown: 0,
    matchShown: 0,
    matchId: null,
  };
}

function load(): GateState {
  if (typeof window === "undefined") return emptyState();
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyState();
    return { ...emptyState(), ...(JSON.parse(raw) as Partial<GateState>) };
  } catch {
    return emptyState();
  }
}

const listeners = new Set<() => void>();

function save(state: GateState) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota errors */
  }
  for (const fn of listeners) fn();
}

export function subscribePollGate(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export interface GateDecision {
  allowed: boolean;
  reason?:
    | "min_gap"
    | "cooldown_dismiss"
    | "cooldown_vote"
    | "cooldown_yield"
    | "session_cap"
    | "match_cap";
  waitMs?: number;
}

export function canShowPoll(opts?: { matchId?: string | null }): GateDecision {
  const state = load();
  const t = now();

  const matchId = opts?.matchId ?? state.matchId;
  if (opts?.matchId && opts.matchId !== state.matchId) {
    state.matchId = opts.matchId;
    state.matchShown = 0;
    save(state);
  }

  if (state.sessionShown >= POLL_CADENCE_POLICY.maxPollsPerSession) {
    return { allowed: false, reason: "session_cap" };
  }
  if (matchId && state.matchShown >= POLL_CADENCE_POLICY.maxPollsPerMatch) {
    return { allowed: false, reason: "match_cap" };
  }

  if (state.cooldownUntil > t) {
    const wait = state.cooldownUntil - t;
    const reason =
      state.cooldownReason === "dismiss"
        ? "cooldown_dismiss"
        : state.cooldownReason === "vote"
          ? "cooldown_vote"
          : state.cooldownReason === "attention_yield"
            ? "cooldown_yield"
            : "min_gap";
    return { allowed: false, reason, waitMs: wait };
  }

  if (state.lastShownAt > 0) {
    const gap = POLL_CADENCE_POLICY.minGapBetweenPollsSec * 1000;
    const elapsed = t - state.lastShownAt;
    if (elapsed < gap) {
      return { allowed: false, reason: "min_gap", waitMs: gap - elapsed };
    }
  }

  return { allowed: true };
}

/** Mark a poll as having just been shown (starts min-gap clock). */
export function notifyPollShown(opts?: { matchId?: string | null }) {
  const state = load();
  const t = now();
  state.lastShownAt = t;
  state.sessionShown += 1;
  if (opts?.matchId) {
    if (opts.matchId !== state.matchId) {
      state.matchId = opts.matchId;
      state.matchShown = 0;
    }
    state.matchShown += 1;
  } else if (state.matchId) {
    state.matchShown += 1;
  }
  state.cooldownReason = "shown";
  save(state);
}

function applyCooldown(seconds: number, reason: Reason) {
  const state = load();
  const until = now() + seconds * 1000;
  if (until > state.cooldownUntil) {
    state.cooldownUntil = until;
    state.cooldownReason = reason;
    save(state);
  }
}

export function notifyPollDismissed() {
  applyCooldown(POLL_CADENCE_POLICY.afterDismissCooldownSec, "dismiss");
}

export function notifyPollVoted() {
  applyCooldown(POLL_CADENCE_POLICY.afterVoteCooldownSec, "vote");
}

export function notifyPollAttentionYield() {
  applyCooldown(POLL_CADENCE_POLICY.afterAttentionYieldCooldownSec, "attention_yield");
}

export function readPollGateSnapshot() {
  const state = load();
  const t = now();
  return {
    ...state,
    msUntilNextAllowed: Math.max(
      state.cooldownUntil - t,
      state.lastShownAt
        ? POLL_CADENCE_POLICY.minGapBetweenPollsSec * 1000 - (t - state.lastShownAt)
        : 0,
    ),
  };
}

export function resetPollGate() {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(STORAGE_KEY);
  for (const fn of listeners) fn();
}

export const POLL_GATE_MIN_VISIBLE_BEFORE_YIELD_MS =
  POLL_CADENCE_POLICY.minVisibleBeforeYieldSec * 1000;

export const POLL_GATE_MIN_POST_VOTE_VISIBLE_MS = 5_000;
